import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, Spinner, Tab, Tabs } from 'react-bootstrap';
import { apiGet, apiPut } from '../services/apiClient';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const statusVariant = {
  connected: 'success',
  disconnected: 'secondary',
  error: 'danger',
};

const randomSecret = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `secret-${Math.random().toString(36).slice(2, 12)}`;
};

const ChannelConnectionDetailPage = ({ token }) => {
  const { id } = useParams();
  const [connection, setConnection] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [secretNotice, setSecretNotice] = useState('');
  const [rateLimit, setRateLimit] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiGet(`/api/admin/channel/connections/${id}`, token);
      setConnection(data);
      setRateLimit(data?.rate_limit_per_min || '');
    } catch (err) {
      setError(err.message || 'Failed to load connection');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const mappings = await apiGet(`/api/admin/channel/mappings?connectionId=${id}`, token);
      const jobLists = await Promise.all(
        (mappings || []).map((mapping) => apiGet(`/api/admin/channel/jobs?mappingId=${mapping.id}`, token))
      );
      const flatJobs = jobLists.flat().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setJobs(flatJobs.slice(0, 100));
      const eventRows = await apiGet(`/api/admin/channel/events?connectionId=${id}`, token);
      setEvents(eventRows || []);
    } catch (_) {
      // ignore logs errors
    }
  };

  useEffect(() => {
    load();
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const webhookUrl = useMemo(() => {
    if (!connection?.provider?.key) return '';
    return `${API_BASE}/api/webhooks/channel/${connection.provider.key}/${connection.id}`;
  }, [connection]);

  const updateRateLimit = async () => {
    try {
      await apiPut(`/api/admin/channel/connections/${id}`, { rate_limit_per_min: Number(rateLimit || 0) }, token);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to update rate limit');
    }
  };

  const regenerateSecret = async () => {
    const newSecret = randomSecret();
    try {
      await apiPut(`/api/admin/channel/connections/${id}`, { webhook_secret: newSecret }, token);
      setSecretNotice(`New webhook secret generated: ${newSecret}`);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to regenerate secret');
    }
  };

  if (loading && !connection) {
    return (
      <div className="py-4 text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!connection) {
    return <Alert variant="danger">Connection not found.</Alert>;
  }

  return (
    <div className="p-4 bg-white rounded shadow-sm">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-0">{connection.label}</h4>
          <small className="text-muted">
            {connection.provider?.name} ({connection.provider?.key})
          </small>
        </div>
        <Badge bg={statusVariant[connection.status] || 'secondary'}>{connection.status}</Badge>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {secretNotice && <Alert variant="success">{secretNotice}</Alert>}

      <Tabs defaultActiveKey="credentials" className="mb-3">
        <Tab eventKey="credentials" title="Credentials">
          <Card className="mb-3">
            <Card.Body>
              <Card.Title className="h6">Masked credentials</Card.Title>
              <pre className="bg-light p-3 rounded small mb-0">
                {JSON.stringify(connection.credentials_masked || {}, null, 2)}
              </pre>
            </Card.Body>
          </Card>
        </Tab>
        <Tab eventKey="webhooks" title="Webhooks">
          <Card className="mb-3">
            <Card.Body>
              <Card.Title className="h6">Webhook endpoint</Card.Title>
              <div className="mb-2">
                <div className="text-muted small">Send provider events to:</div>
                <div className="fw-semibold">{webhookUrl || 'â€”'}</div>
              </div>
              <div className="text-muted small mb-2">
                Secret: {connection.webhook_secret_masked || 'Not set'}
              </div>
              <Button variant="outline-primary" size="sm" onClick={regenerateSecret}>
                Regenerate secret
              </Button>
            </Card.Body>
          </Card>
        </Tab>
        <Tab eventKey="rate" title="Rate limits">
          <Card className="mb-3">
            <Card.Body>
              <Card.Title className="h6">Rate limit settings</Card.Title>
              <Form className="d-flex gap-2 align-items-center">
                <Form.Control
                  type="number"
                  value={rateLimit}
                  onChange={(e) => setRateLimit(e.target.value)}
                  min={1}
                  style={{ maxWidth: 160 }}
                />
                <span className="text-muted">requests per minute</span>
                <Button size="sm" onClick={updateRateLimit}>
                  Save
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Tab>
        <Tab eventKey="logs" title="Logs">
          <div className="mb-3">
            <h6 className="mb-2">Recent jobs</h6>
            <div className="bg-light rounded p-3 small">
              {jobs.length ? (
                jobs.slice(0, 10).map((job) => (
                  <div key={job.id} className="d-flex justify-content-between border-bottom py-2">
                    <div>
                      <div className="fw-semibold">{job.job_type}</div>
                      <div className="text-muted">{job.property_title || 'â€”'}</div>
                    </div>
                    <div className="text-end">
                      <div>{job.status}</div>
                      <div className="text-muted">{job.created_at}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-muted">No jobs yet.</div>
              )}
            </div>
          </div>
          <div>
            <h6 className="mb-2">Recent events</h6>
            <div className="bg-light rounded p-3 small">
              {events.length ? (
                events.slice(0, 10).map((event) => (
                  <div key={event.id} className="d-flex justify-content-between border-bottom py-2">
                    <div>
                      <div className="fw-semibold">{event.event_type}</div>
                      <div className="text-muted">{event.created_at}</div>
                    </div>
                    <div className="text-end">
                      <div>{event.signature_valid ? 'Verified' : 'Unverified'}</div>
                      <div className="text-muted">{event.processed ? 'Processed' : 'Pending'}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-muted">No events yet.</div>
              )}
            </div>
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default ChannelConnectionDetailPage;


